import format from "date-fns/format";

/**
 * Normalizes a computed value for storage in formData.  Date instances are
 * formatted to DateTimeItem's expected string ("yyyy-MM-dd'T'HH:mm") so date
 * inputs don't trip a PropType warning.
 *
 * Non-finite numbers (`NaN` from 0/0 or a bound-but-undefined operand,
 * `±Infinity` from division by zero) are not real results, so they are
 * mapped to `undefined` — the same "no value" signal callers already skip on.
 * Without this, a NaN would render in the field and `JSON.stringify(NaN)`
 * would silently persist `null` on submit, and an inRange check on the value
 * would raise a spurious hard error.  Anything else passes through.
 *
 * An *invalid* Date — `new Date("not a date")`, whose getTime() is NaN — is
 * mapped to `undefined` for the same reason, and this is load-bearing rather
 * than tidy: date-fns `format()` THROWS `RangeError: Invalid time value` on
 * one. Neither call site wraps this in a try/catch, so before the guard a
 * designer expression returning an unparseable date unwound out of the hook
 * and the ErrorBoundary blanked the entire form.
 *
 * Deliberately `undefined` rather than passing the invalid Date through:
 * `useDefaults` calls this with no type gate behind it, so passing it through
 * would store a Date OBJECT into a date item in the live patient flow, trading
 * a crash for a bad stored value. Reporting the mismatch to the designer is
 * `useFunctionRecompute`'s job — it re-checks the raw result so the invalid
 * date is still named in the preview panel. See PR !40046 thread 290104.
 */
export function normalizeComputed(value) {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? undefined
            : format(value, "yyyy-MM-dd'T'HH:mm");
    }
    if (typeof value === "number" && !Number.isFinite(value)) {
        return undefined;
    }
    return value;
}

/**
 * A Number item's designer-configured "decimal digits" as a number, or
 * `undefined` when the item is not a Number or the setting is absent or
 * unusable. This is the `__format` a machine-written value (computation or
 * Imputation default) carries — including a cleared one, since Clinic sets
 * `__format(Decimals)` before nulling the value (quest.viewmodel.js
 * `assignValue`).
 *
 * @param {{ itemType?: string, decimals?: string|null } | undefined} item
 * @returns {number|undefined}
 */
export function configuredDecimals(item) {
    if (
        item?.itemType !== "Number" ||
        item.decimals == null ||
        item.decimals === ""
    ) {
        return undefined;
    }

    const decimals = Number(item.decimals);
    return Number.isInteger(decimals) && decimals >= 0 ? decimals : undefined;
}

/**
 * Applies a Number item's designer-configured decimal digits (`item.decimals`)
 * to a computed/default value before it is stored: the value is rounded to
 * that many decimals (a full-precision `22.642192…` becomes `22.6` on a
 * 1-decimal item) and the SETTING ITSELF is returned as the `__format`
 * sidecar. Clinic pads machine-written numbers to the design decimals
 * (quest.viewmodel.js `assignValue` does `__format(Decimals)` +
 * `toFixed(Decimals)`), and so does the server when it re-runs functions
 * (`ParseAndSetPrecision`) — a whole-number result reads "25.0" there, so the
 * format must say 1 here too or the same stored value renders differently in
 * the two products. Typed values keep the digit count the patient entered
 * (NumberInput.handleChange) and never pass through here.
 *
 * Anything that is not a Number item with configured decimals — or not a
 * finite numeric value — passes through unchanged with no format, so callers
 * can apply this unconditionally.
 *
 * @param {*} value  value to store, already through `normalizeComputed`
 * @param {{ itemType?: string, decimals?: string|null } | undefined} item
 * @returns {{ value: *, format: number|undefined }}
 */
export function applyNumberPrecision(value, item) {
    const decimals = configuredDecimals(item);
    if (decimals === undefined) {
        return { value, format: undefined };
    }

    const numeric =
        typeof value === "number"
            ? value
            : typeof value === "string" && value.trim() !== ""
              ? Number(value)
              : Number.NaN;
    if (!Number.isFinite(numeric)) {
        return { value, format: undefined };
    }

    return { value: Number(numeric.toFixed(decimals)), format: decimals };
}
